import * as React from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { c } from 'ttag';

import { ArrowLeft, Close } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { Button, Tooltip } from '../../index';

interface Props {
    open: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    onBack?: () => void;
    size?: number;
    /**
     * Extra props for the panel, a `className` among them.
     *
     * **A className given here is ADDED to the component's own, never substituted.**
     * It used to replace it: the props were spread after `className`, so a caller
     * styling the panel silently removed `modal_panel-container` and with it the
     * width, the radius and the centring. The box then rendered at zero by zero,
     * behind a backdrop that showed perfectly well, which is a failure that looks like
     * an empty modal rather than like a lost class.
     */
    panelProps?: { className?: string } & Record<string, unknown>;
    closeButtonProps?: object;
    /**
     * Whether the reader may dismiss this modal. `true` by default, which is what a
     * modal normally is.
     *
     * **`false` is for a CONDITION, not for an important message**, and the difference
     * decides whether this prop is the right one. A warning one must read stays
     * dismissable: the reader has read it. A condition of entry is not read, it is
     * met: an activation to set up, a document to accept. Hiding the cross while
     * leaving Escape and the backdrop working would be worse than either, since the
     * modal would look sealed and open on the first stray key.
     *
     * Whoever passes `false` owes the reader another way out **inside** the modal, be
     * it « Se déconnecter » or « Annuler la démarche ». A dialog with no exit at all
     * is a trap, and no prop can make that acceptable.
     */
    dismissable?: boolean;
}

const Modal = ({
    open,
    onClose,
    onBack,
    title,
    children,
    size,
    panelProps,
    closeButtonProps,
    dismissable = true,
}: Props) => {
    const style = { ...(size && { maxInlineSize: `${size}px` }) };
    return (
        <Transition show={open} as={React.Fragment}>
            <Dialog open={open} onClose={() => dismissable && onClose()} className="modal">
                <Transition.Child
                    as={React.Fragment}
                    enter="modal_backdrop-enter"
                    enterFrom="modal_backdrop-enterFrom"
                    enterTo="modal_backdrop-enterTo"
                    leave="modal_backdrop-leave"
                    leaveFrom="modal_backdrop-leaveFrom"
                    leaveTo="modal_backdrop-leaveTo"
                >
                    <div className="modal_backdrop" aria-hidden="true" />
                </Transition.Child>
                <Transition.Child
                    as={React.Fragment}
                    enter="modal_panel-enter"
                    enterFrom="modal_panel-enterFrom"
                    enterTo="modal_panel-enterTo"
                    leave="modal_panel-leave"
                    leaveFrom="modal_panel-leaveFrom"
                    leaveTo="modal_panel-leaveTo"
                >
                    <Dialog.Panel
                        as="dialog"
                        style={style}
                        {...panelProps}
                        className={clsx('modal_panel-container', panelProps?.className)}
                    >
                        <main className="modal_panel">
                            <header>
                                <Dialog.Title>
                                    {onBack && (
                                        <Button onClick={onBack} size="small" category="ghost" icon>
                                            <ArrowLeft />
                                        </Button>
                                    )}
                                    {title}
                                </Dialog.Title>
                                {dismissable && (
                                    <Tooltip tabIndex={0} title={c('Modal Tooltip').t`Close`}>
                                        <Button
                                            onClick={() => onClose()}
                                            icon
                                            size="small"
                                            category="ghost"
                                            {...closeButtonProps}
                                        >
                                            <Close />
                                        </Button>
                                    </Tooltip>
                                )}
                            </header>
                            {children}
                        </main>
                    </Dialog.Panel>
                </Transition.Child>
            </Dialog>
        </Transition>
    );
};

export default Modal;
